'use client';

import { API_ROUTES, QueriesBoundary, useApiQuery } from '@shared/api-utils';
import { useDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { GamesApiResponse } from '../../casino/types/types';
import { PromotionsContent, PromotionsContentSkeleton, PromotionsEmpty, PromotionsList } from '../components';
import type { FreeSpinsOfferData, PromoType } from '../types/types';
import { getPromotionUrls } from '../utils/promotionApiMapper';
import { mapFreeSpins } from './utils/mapFreeSpins/mapFreeSpins';

type PromoContentFreeSpinsProps = ComponentProps<'div'> & {
  namespace: string;
};

type ContentProps = {
  namespace: string;
  games: GamesApiResponse;
  activeFS: FreeSpinsOfferData;
  nonActiveFS: FreeSpinsOfferData;
};

const PROMOTION_TYPE = 'free-spins' satisfies PromoType;

function Content({ namespace, activeFS, nonActiveFS, games }: ContentProps) {
  const { baseImgUrl: baseImageUrl } = useClientConfig();
  const t = useTranslations(namespace);
  const debug = useDebug();

  const mappedActiveFS = activeFS.list.map(item => mapFreeSpins({ item, t, games: games?.games ?? [], baseImageUrl, debug }));
  const mappedNonActiveFS = nonActiveFS.list.map(item => mapFreeSpins({ item, t, games: games?.games ?? [], baseImageUrl, debug }));

  return (
    <>
      {mappedActiveFS?.length ? <PromotionsList promoType={PROMOTION_TYPE} data={mappedActiveFS} /> : <PromotionsEmpty promoType={PROMOTION_TYPE} />}
      {mappedNonActiveFS?.length ? <PromotionsList promoType={PROMOTION_TYPE} data={mappedNonActiveFS} showTitle grayscale /> : null}
    </>
  );
}

function PromoContentFreeSpins({ namespace, ...props }: PromoContentFreeSpinsProps) {
  const activeFSQuery = useApiQuery<FreeSpinsOfferData, FreeSpinsOfferData>(getPromotionUrls('free-spins', { status: 'active', offset: '0', limit: '10' }).list, {
    credentials: true,
    queryOptions: { staleTime: 0 },
  });
  const nonActiveFSQuery = useApiQuery<FreeSpinsOfferData, FreeSpinsOfferData>(getPromotionUrls('free-spins', { status: 'non-active', offset: '0', limit: '5' }).list, {
    credentials: true,
    queryOptions: { staleTime: 0 },
  });
  const { currency, apiLocale } = useClientConfig();
  const gamesQuery = useApiQuery<GamesApiResponse, GamesApiResponse>(API_ROUTES.GAMES.GAMES_LIST.replace(':locale', apiLocale).replace(':currency', currency));
  const debug = useDebug();

  return (
    <PromotionsContent promoType={PROMOTION_TYPE} {...props}>
      <QueriesBoundary
        queries={[activeFSQuery, nonActiveFSQuery, gamesQuery]}
        loadingFallback={<PromotionsContentSkeleton />}
        errorFallbackRender={() => {
          debug.error(`Fetching failed in PromoContentFreeSpins. Check the queries.`);
          return <PromotionsEmpty promoType={PROMOTION_TYPE} />;
        }}
      >
        {([{ data: activeFS }, { data: nonActiveFS }, { data: games }]) => <Content namespace={namespace} activeFS={activeFS} nonActiveFS={nonActiveFS} games={games} />}
      </QueriesBoundary>
    </PromotionsContent>
  );
}

export { Content, PromoContentFreeSpins };
